import {
	CartesianGrid,
	Legend,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

interface TimeSeriesPoint {
	timestamp: string;
	avgLatencyMs: number | null;
}

interface LatencyChartProps {
	data: TimeSeriesPoint[];
	isLoading?: boolean;
}

export function LatencyChart({ data, isLoading }: LatencyChartProps) {
	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Response Latency</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-64 flex items-center justify-center">
						<div className="text-sm text-muted-foreground">
							Loading chart data...
						</div>
					</div>
				</CardContent>
			</Card>
		);
	}

	if (!data || data.length === 0) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Response Latency</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-64 flex items-center justify-center">
						<div className="text-sm text-muted-foreground">
							No data available
						</div>
					</div>
				</CardContent>
			</Card>
		);
	}

	const formattedData = data
		.filter((point) => point.avgLatencyMs !== null)
		.map((point) => ({
			...point,
			latency: point.avgLatencyMs ?? 0,
			label: point.timestamp.includes("T")
				? `${new Date(point.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${new Date(point.timestamp).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`
				: new Date(point.timestamp).toLocaleDateString("en-US", {
						month: "short",
						day: "numeric",
					}),
		}));

	if (formattedData.length === 0) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Response Latency</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-64 flex items-center justify-center">
						<div className="text-sm text-muted-foreground">
							No latency data available
						</div>
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-lg">Response Latency</CardTitle>
			</CardHeader>
			<CardContent>
				<ResponsiveContainer width="100%" height={300}>
					<LineChart
						data={formattedData}
						margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
					>
						<CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
						<XAxis
							dataKey="label"
							className="text-xs"
							tick={{ fill: "currentColor" }}
							angle={-45}
							textAnchor="end"
							height={60}
						/>
						<YAxis className="text-xs" tick={{ fill: "currentColor" }} />
						<Tooltip
							contentStyle={{
								backgroundColor: "hsl(var(--card))",
								border: "1px solid hsl(var(--border))",
								borderRadius: "6px",
							}}
							labelStyle={{ color: "hsl(var(--foreground))" }}
							formatter={(value: any) => [
								`${value !== undefined ? Number(value).toFixed(0) : "0"} ms`,
								"Latency",
							]}
						/>
						<Legend />
						<Line
							type="monotone"
							dataKey="latency"
							stroke="hsl(var(--primary))"
							strokeWidth={2}
							dot={false}
							name="Avg Latency (ms)"
						/>
					</LineChart>
				</ResponsiveContainer>
			</CardContent>
		</Card>
	);
}
